// Офлайн-очередь: закрытие точки и завершение смены, если связи нет.
//
// Курьер в подвале магазина нажал «Отметить выполненной» — действие ложится в
// очередь с ключом clientRequestId и реальным временем; отправляем по порядку,
// как только появится сеть. Сервер узнаёт повтор по ключу, поэтому двойная
// отправка ничего не задвоит. Закрытия точек всегда уходят раньше завершения
// смены — иначе сервер не нашёл бы смену для них.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import toast from 'react-hot-toast';

import { apiErrorMessage, isNetworkError } from '@/lib/api';
import { draftFromBody, saveDraft } from '@/lib/drafts';
import { deletePhoto, getPhoto } from '@/lib/idb';
import { flushTrack } from '@/lib/track';
import { closePoint, finishShift, uploadPointPhoto, type VisitCloseBody } from '@/services/courier';

export type QueuedAction =
  | {
      id: string;
      kind: 'close';
      shiftId: string;
      pointId: string;
      pointName: string;
      body: VisitCloseBody;
      createdAt: number;
      /** Фото витрины ещё в телефоне (IndexedDB) — отправить перед закрытием. */
      photoPending?: boolean;
      /** С какого момента сервер отвечает сбоем (5xx) — через 30 минут сдаёмся. */
      failingSince?: number;
    }
  | {
      id: string;
      kind: 'finish';
      shiftId: string;
      body: { comment?: string | null; clientRequestId: string; report?: Array<{ itemId: string; leftover: number }> };
      createdAt: number;
      failingSince?: number;
    };

interface QueueState {
  items: QueuedAction[];
  flushing: boolean;
  add: (a: QueuedAction) => void;
  remove: (id: string) => void;
  patch: (id: string, p: Partial<QueuedAction>) => void;
}

export const useQueue = create<QueueState>()(
  persist(
    (set) => ({
      items: [],
      flushing: false,
      add: (a) => set((s) => ({ items: [...s.items.filter((x) => x.id !== a.id), a] })),
      remove: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
      patch: (id, p) => set((s) => ({ items: s.items.map((x) => (x.id === id ? ({ ...x, ...p } as QueuedAction) : x)) })),
    }),
    { name: 'resstro-courier-queue:v1', partialize: (s) => ({ items: s.items }) },
  ),
);

let onSynced: (() => void) | null = null;

/** Сбой сервера дольше этого — уже не перезапуск, а ошибка: возвращаем в черновик. */
const GIVE_UP_5XX_MS = 30 * 60_000;

/**
 * Сервер занят или перезапускается — повторим позже, ничего не теряя:
 * 408/425/429 — всегда, 5xx — пока сбой длится меньше 30 минут.
 */
function isTransient(status: number | undefined, action: QueuedAction): boolean {
  if (status === 408 || status === 425 || status === 429) return true;
  if (status == null || status < 500) return false;
  return Date.now() - (action.failingSince ?? Date.now()) < GIVE_UP_5XX_MS;
}

/** Что обновить после успешной отправки (react-query). */
export function setQueueSyncedHandler(fn: () => void): void {
  onSynced = fn;
}

let inflight: Promise<boolean> | null = null;

/** Отправить всё, что ждёт. true — очередь пуста. Параллельный вызов ждёт идущую отправку. */
export function flushQueue(): Promise<boolean> {
  if (!inflight) {
    inflight = runFlush().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

async function runFlush(): Promise<boolean> {
  if (useQueue.getState().items.length === 0) return true;
  useQueue.setState({ flushing: true });
  let sent = 0;
  try {
    for (const action of [...useQueue.getState().items]) {
      try {
        if (action.kind === 'close') {
          if (action.photoPending) {
            const key = `${action.shiftId}:${action.pointId}`;
            const photo = await getPhoto(key);
            if (photo) await uploadPointPhoto(action.pointId, photo.blob, photo.name);
            await deletePhoto(key).catch(() => undefined);
            useQueue.getState().patch(action.id, { photoPending: false });
          }
          await closePoint(action.pointId, action.body);
        } else {
          // Хвост GPS-трека — пока смена ещё открыта (иначе руководитель не увидит конец маршрута).
          await flushTrack({ force: true });
          await finishShift(action.body);
        }
        useQueue.getState().remove(action.id);
        sent += 1;
      } catch (err) {
        if (isNetworkError(err)) break;
        const status = (err as { response?: { status?: number } }).response?.status;
        // «Подождите, сохраняется» — сервер ещё занят прошлой попыткой: повторим позже.
        if (status === 409 && /сохраняется/i.test(apiErrorMessage(err))) break;
        if (status === 401) break;
        if (isTransient(status, action)) {
          if (status != null && status >= 500 && action.failingSince == null) useQueue.getState().patch(action.id, { failingSince: Date.now() });
          break;
        }
        useQueue.getState().remove(action.id);
        // Набранное возвращаем в черновик точки: поправить и отметить снова.
        if (action.kind === 'close') saveDraft(action.shiftId, action.pointId, draftFromBody(action.body));
        toast.error(
          action.kind === 'close'
            ? `«${action.pointName}» не сохранена: ${apiErrorMessage(err)}`
            : `Смена не завершена: ${apiErrorMessage(err)}`,
          { duration: 8000 },
        );
      }
    }
  } finally {
    useQueue.setState({ flushing: false });
  }
  if (sent > 0) onSynced?.();
  return useQueue.getState().items.length === 0;
}
